import { Card } from '@/components/ui/card'
import { ChevronUp, Loader2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { Badge } from '@/components/ui/badge'

interface Tag {
	id: string
	name: string
	color?: string | null
}

interface Feature {
	id: string
	title: string
	description: string
	status: string
	upvote_count: number
	created_at: string
	tags: Tag[]
}

interface PublicFeatureCardProps {
	feature: Feature
	onUpvote: (featureId: string) => void
	hasUpvoted: boolean
	isVoting: boolean
	onFeatureClick: (featureId: string) => void
}

export function PublicFeatureCard({
	feature,
	onUpvote,
	hasUpvoted,
	isVoting,
	onFeatureClick,
}: PublicFeatureCardProps) {
	const handleUpvoteClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!isVoting) {
			onUpvote(feature.id)
		}
	}

	const handleCardClick = () => {
		onFeatureClick(feature.id)
	}

	return (
		<motion.div whileHover={{ y: -3 }} transition={{ duration: 0.1 }}>
			<Card
				onClick={handleCardClick}
				className={cn(
					'group w-full cursor-pointer overflow-hidden rounded-lg border bg-white shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md dark:bg-slate-800/50 dark:border-slate-700/50 dark:hover:border-slate-600'
				)}>
				<div className="flex">
					<div className="flex-1 p-4 flex flex-col justify-between">
						<div>
							<p className="mb-2 text-sm font-medium text-gray-800 dark:text-gray-200">
								{feature.title}
							</p>

							<p className="text-xs text-gray-400 dark:text-gray-500">
								{formatDistanceToNow(new Date(feature.created_at), {
									addSuffix: true,
								})}
							</p>
						</div>

						{feature.tags.length > 0 && (
							<div className=" flex flex-wrap gap-1 mt-4">
								{feature.tags.map((tag) => (
									<Badge
										key={tag.id}
										className={``}
										style={
											tag.color
												? {
														backgroundColor: `${tag.color}15`,
														color: tag.color,
												  }
												: undefined
										}>
										{tag.name}
									</Badge>
								))}
							</div>
						)}
					</div>

					<div
						className={cn(
							'flex w-16 flex-shrink-0 flex-col items-center justify-center border-l transition-colors dark:border-l-slate-700',
							hasUpvoted
								? 'bg-primary/10'
								: 'bg-gray-50 group-hover:bg-gray-100 dark:bg-slate-800 dark:group-hover:bg-slate-700'
						)}
						onClick={handleUpvoteClick}
						aria-disabled={isVoting}
						role="button"
						tabIndex={0}>
						{isVoting ? (
							<Loader2 className="h-4 w-4 animate-spin text-gray-500" />
						) : (
							<>
								<ChevronUp
									className={cn(
										'h-5 w-5',
										hasUpvoted
											? 'text-primary'
											: 'text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200'
									)}
								/>
								<span
									className={cn(
										'text-sm font-semibold',
										hasUpvoted
											? 'text-primary'
											: 'text-gray-600 group-hover:text-gray-800 dark:text-gray-300 dark:group-hover:text-gray-100'
									)}>
									{feature.upvote_count}
								</span>
							</>
						)}
					</div>
				</div>
			</Card>
		</motion.div>
	)
}
